import { Injectable, inject, effect, signal, computed } from '@angular/core'
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular'
import Keycloak, { KeycloakProfile } from 'keycloak-js'
import { User } from '../types/User'

@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly keycloakEventSignal = inject(KEYCLOAK_EVENT_SIGNAL)
    private readonly keycloakService = inject(Keycloak)

    readonly userProfile = signal<KeycloakProfile | null>(null)
    readonly userRoles = signal<string[]>([])
    readonly currentUser = computed<User>(() => ({
        roles: this.userRoles(),
        profile: this.userProfile() || {}
    }))

    readonly isAuthenticatedSignal = computed(() => this.keycloakService.authenticated === true)
    private profileLoaded = false

    constructor() {
        if (this.keycloakService.authenticated) {
            this.refreshUserRoles()
            this.loadUserProfileIfNeeded()
        }

        effect(() => {
            const event = this.keycloakEventSignal()

            switch (event.type) {
                case KeycloakEventType.AuthSuccess:
                    this.refreshUserRoles()
                    this.loadUserProfileIfNeeded()
                    this.profileLoaded = true
                    break
                case KeycloakEventType.AuthRefreshSuccess:
                    this.refreshUserRoles()
                    break
                case KeycloakEventType.AuthLogout:
                    this.clearUserState()
                    this.profileLoaded = false
                    break
            }
        })
    }

    private async loadUserProfileIfNeeded(): Promise<void> {
        if (!this.profileLoaded && this.userProfile() === null && this.keycloakService.authenticated) {
            try {
                const profile = await this.keycloakService.loadUserProfile()
                this.userProfile.set(profile)
                this.profileLoaded = true
            } catch (error) {
                console.error('Failed to load user profile:', error)

                this.userProfile.set(null)
                this.profileLoaded = false
                this.keycloakService.logout()

                //refresh the page to clear the session
                if (window.location.href.indexOf('login') === -1) {
                    window.location.reload()
                }
            }
        }
    }

    private clearUserState(): void {
        this.userProfile.set(null)
        this.userRoles.set([])
        this.profileLoaded = false
    }

    private refreshUserRoles(): void {
        const roles = this.extractRolesFromToken()
        this.userRoles.set(roles)
    }

    private extractRolesFromToken(): string[] {
        const tokenParsed = this.keycloakService.tokenParsed
        const realmRoles = tokenParsed?.realm_access?.roles || []

        const resourceRoles: string[] = []
        if (tokenParsed?.resource_access) {
            Object.keys(tokenParsed.resource_access).forEach((resource) => {
                const roles = tokenParsed.resource_access[resource]?.roles || []
                resourceRoles.push(...roles)
            })
        }

        return [...realmRoles, ...resourceRoles]
    }

    logout() {
        this.keycloakService.logout()
    }

    get isAuthenticated(): boolean {
        return this.isAuthenticatedSignal()
    }
}
